'use client'

import { navigateUrl } from "@/utils/client-request"
import { formatPrice } from "@/utils/util"
import { Minus, Plus } from "lucide-react"
import { useCartItem } from "@/lib/commerce/hooks"

const focusRing = 'focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-foreground'

export default function CartItemSidebar({ product, pathname }: { product: any, pathname: string }) {
  const c = useCartItem(product)
  const productHref = navigateUrl(`/product/${c.productCustomId}`, pathname)

  const tags = [
    c.productData?.isDigital ? 'Дижитал' : null,
    c.isReward ? 'Бэлэг' : null,
    c.showDiscountedPrice && c.discountName ? c.discountName : null,
    c.loyaltyDeal ? `Лоялти -${c.loyaltyDeal.percent}%` : null,
  ].filter(Boolean) as string[]

  return (
    <div className="flex gap-4">
      <a href={productHref} className={`w-20 shrink-0 ${focusRing}`}>
        <div className="aspect-3/4 bg-muted overflow-hidden">
          <img src={c.imageUrl} alt={c.productName} className="w-full h-full object-cover object-center" />
        </div>
      </a>

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <a href={productHref} className={`min-w-0 ${focusRing}`}>
            <h3 className="text-[13px] sm:text-sm leading-snug line-clamp-2 wrap-break-word">{c.productName}</h3>
          </a>
          <div className="shrink-0 text-right text-[13px] sm:text-sm tabular-nums">
            {c.showDiscountedPrice ? (
              <>
                <div>{c.unitPrice === 0 ? 'Үнэгүй' : formatPrice(c.displayPrice)}</div>
                <div className="text-xs line-through text-muted-foreground">{formatPrice(c.qty * c.originalPrice!)}</div>
              </>
            ) : (
              <div>{formatPrice(c.displayPrice)}</div>
            )}
          </div>
        </div>

        {c.showVariantName && (
          <div className="mt-1 text-xs text-muted-foreground">
            {c.variant.name}
            {c.dimension && <span> · {c.dimension}</span>}
          </div>
        )}
        {tags.length > 0 && (
          <div className="mt-1 text-xs text-muted-foreground line-clamp-1">{tags.join(' · ')}</div>
        )}

        <div className="mt-auto pt-3 flex items-center justify-between gap-3">
          {c.isReward ? (
            <span className="text-xs text-muted-foreground tabular-nums">Тоо: {c.qty}</span>
          ) : c.isOutOfStock ? (
            <span className="text-xs text-foreground">Бараа дууссан</span>
          ) : (
            <div className="inline-flex items-center h-8 border border-border">
              <button type="button" onClick={c.handleRemoveOne} disabled={c.loading} aria-label="Хасах" className={`h-full w-8 grid place-items-center transition-colors duration-150 hover:bg-foreground/5 disabled:opacity-40 ${focusRing}`}>
                <Minus className="size-3" strokeWidth={1.5} />
              </button>
              <span className="min-w-8 text-center text-xs tabular-nums">{c.qty}</span>
              <button type="button" onClick={c.handleAddOne} disabled={c.loading} aria-label="Нэмэх" className={`h-full w-8 grid place-items-center transition-colors duration-150 hover:bg-foreground/5 disabled:opacity-40 ${focusRing}`}>
                <Plus className="size-3" strokeWidth={1.5} />
              </button>
            </div>
          )}
          <button type="button" onClick={c.handleRemove} disabled={c.loading} className={`text-xs text-muted-foreground underline-offset-4 transition-colors duration-150 hover:text-foreground hover:underline disabled:opacity-40 ${focusRing}`}>
            Устгах
          </button>
        </div>
      </div>
    </div>
  )
}
